import { useApiMutation } from "@gladis/api-client";
import type { MemberDto } from "@gladis/shared";
import { EmptyState } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { EXPENSES_KEY, type CardSummaryDto } from "./ExpenseList.js";
import { PaymentCard } from "./PaymentCard.js";

/**
 * The household's payment sources, laid out as a set you look ACROSS rather than a list you read down
 * (FR-049).
 *
 * WHY THAT IS A REQUIREMENT AND NOT A PREFERENCE. The question a member brings to this section is
 * comparative — which card carries the most, whose card is it, is the one I am about to pick the joint
 * one. A vertical list answers "what is the next card" and makes the comparison a scroll; a wrapping
 * row puts the whole set in one glance, which is what a wallet does.
 *
 * A `<ul>` with `list-none`, not a bare `<div>` of cards. The visual layout is a row; the SEMANTIC
 * structure is still a list of four things, and that is what a screen reader announces — "list, 4
 * items" — before reading the first one. Losing that would make the set an undifferentiated run of
 * headings.
 *
 * NO PANEL, NO HEADING OF ITS OWN. This lived inside a bordered panel headed "Cards" while it was one
 * section among several on the Expenses page, where the heading was what told a member which of four
 * things they were looking at. On its own page the page title says "Cards", so the panel was saying it
 * twice and framing the set in a box it no longer needs — the cards are already objects with edges, and
 * a border around a row of bordered objects is just another line.
 *
 * The consequence for assistive technology is deliberate rather than accepted: the `<h1>` names this
 * content, so a `<section aria-labelledby>` pointing at a second, redundant heading would announce a
 * region called "Cards" inside a page called "Cards".
 *
 * KEYBOARD REACHABILITY IS AN ORDINARY CONSEQUENCE HERE (FR-055): every action is a real `<button>` in
 * document order inside its card, so tabbing goes card by card with no `tabindex` anywhere. The focus
 * ring is explicit rather than inherited, because the cards sit on a tinted gradient where the
 * browser's default outline is close to invisible.
 *
 * AND NO "ADD A CARD" CONTROL EITHER, which is the same argument one step further. Creating a card is
 * the PAGE'S primary action, and the app gives every page's primary action one standard position — the
 * end of the title row — so a button here would be the same action in a second place on the same
 * screen, sliding down the page whenever the form opened above it. This component describes and lists;
 * it does not own the verb. Editing and archiving stay, because those two are per-card and mean nothing
 * away from the card they are attached to.
 *
 * NEITHER OF THEM IS A DOTS MENU, which is the one place this deliberately parts company with 019's rule
 * that a row's actions sit behind one `ActionMenu`. That rule earns its keep on a TABLE, where two text
 * buttons make a column as wide as their two labels and put a row's controls in competition with its
 * figures. A card is not a row: it has a footer with room beside the monthly total, the two labels cost
 * nothing that is otherwise being read, and a menu here would spend a click on opening a list of two —
 * one of which is the thing the member is looking straight at. `PaymentCard` reveals them on hover
 * instead, so the resting card stays the wallet object it is drawn to be.
 */

export function CardSet({
  cards,
  members = [],
  onEdit,
}: {
  cards: readonly CardSummaryDto[];
  /** The household's members, for each card owner's face (020). */
  members?: readonly MemberDto[];
  /**
   * Opens the form on an existing card. Omitted, no card offers an edit control at all.
   *
   * A CALLBACK RATHER THAN A FORM RENDERED HERE, for the reason `ExpenseList.onEdit` gives: the route owns
   * where a form appears — it already owns where the create form appears — and this component's job is to
   * say which card. Rendering a second `CardForm` per card would also mount one dialog per card in the set.
   */
  onEdit?: (card: CardSummaryDto) => void;
}): ReactNode {
  const t = useT();
  /*
   * ACTIVE FIRST, THEN ARCHIVED, each alphabetically.
   *
   * An archived card is still shown — its expenses keep counting and keep settling (FR-043), so hiding
   * it would leave a household total nothing on the page accounts for. It is shown LAST because it is
   * not one of the cards a member is choosing between today.
   */
  const ordered = [...cards].sort(byStateThenName);

  // No wrapper element and no top margin: with the panel, the heading and the button row all gone,
  // this component IS the set, and the gap above it belongs to whatever lays it out.
  if (ordered.length === 0) {
    return <EmptyState>{t("exp.card.empty")}</EmptyState>;
  }

  return (
    /*
     * A GRID WITH BOUNDED TRACKS, and the bound is the fix rather than a preference.
     *
     * This was `flex-wrap` with `flex-1` on each item, which makes every item share the row equally — so
     * with TWO cards on a wide screen each item became about 550px while `PaymentCard` is capped at 26rem.
     * The card sat at its cap and the item kept the remainder, which reads as an enormous gap between the
     * two. Four cards hid it, because the items were then narrower than the cap.
     *
     * `auto-fill` with `minmax(17rem, 22rem)` fixes the CARD width instead of the item count: cards are the
     * same size whether there are two or nine, they pack from the left, and the space left over on a wide
     * screen falls after the last card rather than between them. The upper bound matters as much as the
     * lower: the cards have a fixed aspect ratio, so a track that could stretch would make them taller than
     * their neighbours as well as wider.
     */
    <ul className="grid list-none grid-cols-[repeat(auto-fill,minmax(17rem,22rem))] gap-5 p-0">
      {ordered.map((card) => (
        <li key={card.id}>
          <PaymentCard
            card={card}
            members={members}
            actions={
              <>
                {/*
                  EDIT FIRST, ARCHIVE SECOND, and the order is the reading order rather than a preference:
                  correcting a card is the ordinary act and retiring one is the exception, so the exception
                  is the further of the two from the member's pointer as it arrives at the footer.
                */}
                {onEdit === undefined ? null : <CardEditButton card={card} onEdit={onEdit} />}
                <CardArchiveButton card={card} />
              </>
            }
          />
        </li>
      ))}
    </ul>
  );
}

function byStateThenName(left: CardSummaryDto, right: CardSummaryDto): number {
  const leftArchived = left.archivedAt !== null;
  const rightArchived = right.archivedAt !== null;
  if (leftArchived !== rightArchived) return leftArchived ? 1 : -1;
  return left.name.localeCompare(right.name);
}

/**
 * The look both card actions share, in one place because they sit next to each other and a card's footer
 * is the one row in the app where two controls are read as a pair.
 */
const CARD_ACTION_CLASS =
  "rounded-control px-1 text-muted underline decoration-dotted hover:text-ink " +
  "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50";

/**
 * Correct this card.
 *
 * It carries NO MUTATION — it hands the card up and the route opens the form on it. Everything that could
 * be got wrong about a card lives on that form, including the preview a member checks the four digits
 * against, and a button that wrote anything directly would be a second way to change a card with none of
 * that in front of it.
 *
 * The accessible name includes the CARD'S NAME, for the reason `CardArchiveButton` gives below.
 */
function CardEditButton({
  card,
  onEdit,
}: {
  card: CardSummaryDto;
  onEdit: (card: CardSummaryDto) => void;
}): ReactNode {
  const t = useT();
  return (
    <button
      type="button"
      // Opens rather than toggles: the form is a modal with three ways to close and this is not one of
      // them. `aria-haspopup` rather than the `aria-expanded` a toggle would carry — the same distinction
      // the page's own "Add a card" button makes.
      aria-haspopup="dialog"
      onClick={() => {
        onEdit(card);
      }}
      className={CARD_ACTION_CLASS}
    >
      <span aria-hidden="true">{t("common.edit")}</span>
      <span className="sr-only">Edit {card.name}</span>
    </button>
  );
}

/**
 * Archive, or put back.
 *
 * Its own component so the mutation hook is per-card, for the same reason `ArchiveButton` is per-row in
 * `ExpenseList`: one hook driving whichever card was clicked last means the second of two rapid clicks
 * carries the first card's pending state.
 *
 * The accessible name includes the CARD'S NAME. Four buttons all reading "Archive" is four identical
 * accessible names in one list, and a screen reader user tabbing through them has no way to tell which
 * card they are about to archive. The visible label stays short.
 */
function CardArchiveButton({ card }: { card: CardSummaryDto }): ReactNode {
  const t = useT();
  const verb = card.archivedAt === null ? "archive" : "unarchive";

  const run = useApiMutation<void, void>(
    (client) => client.post<void>(`/cards/${card.id}/${verb}`, {}),
    { invalidates: [EXPENSES_KEY] },
  );

  return (
    <button
      type="button"
      onClick={() => {
        run.mutate();
      }}
      disabled={run.isPending}
      className={CARD_ACTION_CLASS}
    >
      <span aria-hidden="true">
        {verb === "archive" ? t("exp.card.archive") : t("exp.card.restore")}
      </span>
      <span className="sr-only">
        {verb === "archive" ? `Archive ${card.name}` : `Restore ${card.name}`}
      </span>
    </button>
  );
}
